import { camelCase, fromPairs } from 'lodash';
import { useMemo } from 'react';
import { FieldSectionType } from 'api/field-sections/types';
import { User } from 'api/models';
import { Field, FieldOption, FieldValue, FieldValueType } from 'api/tasks';
import { Option } from 'components/atoms/select/types';
import { FieldSectionMapping } from 'components/organisms/field-sections/section-list';
import { computeSectionFieldMapping } from 'components/organisms/field-sections/utils';
import { canShowInTeamMemberFieldsSettings } from 'components/organisms/module-settings/fields/utils';
import { addValuesToFields } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields';
import {
  FieldType,
  FilterableField,
  filterableFieldType,
  SourceType,
} from 'components/organisms/tasks/fields';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import { useTeamMemberFieldsQuery } from 'queries';
import { useFieldSectionsQuery } from 'queries/field-sections';
import { useNativeMembersQuery } from 'queries/members';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';

const userOption = (user: User): Option => ({
  label: fullName(user),
  value: user.userId,
  color: getRandomColor(user.userId),
  description: user.role?.roleName,
  imageUrl: user.photoUrl,
  logoUrl: user.company.companyLogoUrl,
});

const usePopulateUsersMetaInFields = (fields: Field[]): Field[] => {
  const { data } = useNativeMembersQuery();
  const { userIdMap, userIds, userOptions } = useMemo(
    () => ({
      userIds: (data?.users ?? []).map((x) => x.userId),
      userOptions: (data?.users ?? []).map(userOption) as FieldOption<number>[],
      userIdMap: fromPairs(data?.users?.map((x) => [x.userId, x])),
    }),
    [data?.users],
  );

  return useMemo(
    () =>
      (fields ?? []).map((field) => {
        const type = filterableFieldType(field);

        if (type === FieldType.MultiUser || type === FieldType.SingleUser) {
          return {
            ...field,
            metaData: {
              ...field.metaData,
              options: userOptions,
              users: {
                by_user_id: userIdMap,
                active_user_ids: userIds,
                non_external_user_ids: userIds,
                by_project_id: {},
              },
            },
            sourceType: SourceType.User,
          };
        }

        return field;
      }),
    [fields, userIdMap, userIds, userOptions],
  );
};

interface UserFieldSectionProps {
  isLoading: boolean;
  fieldSections: any;
  fieldSectionMapping: FieldSectionMapping[];
}

export const useUserFieldSectionMapping = (
  userFieldsWithValue: Array<FieldValue<FieldValueType>> | undefined,
): UserFieldSectionProps => {
  const { data: userFields } = useTeamMemberFieldsQuery();

  const filteredUserFieldsList = useMemo(
    () =>
      userFields?.filter(
        (field) => canShowInTeamMemberFieldsSettings(field) && field.isEnabled,
      ),
    [userFields],
  );

  const filteredUserFields = usePopulateUsersMetaInFields(
    filteredUserFieldsList ?? [],
  );

  const { data: fieldSections, isLoading } = useFieldSectionsQuery(
    FieldSectionType.Users,
  );

  const fieldsWithValues = useMemo(
    () => addValuesToFields(filteredUserFields, userFieldsWithValue ?? []),
    [filteredUserFields, userFieldsWithValue],
  );

  const fieldSectionMapping = useMemo(() => {
    const sectionFieldMapping = computeSectionFieldMapping(
      fieldsWithValues,
      fieldSections,
    );

    return sectionFieldMapping;
  }, [fieldsWithValues, fieldSections]);

  return {
    isLoading,
    fieldSections,
    fieldSectionMapping,
  };
};

export const useUserCustomFieldsAsFilterableFields = (): {
  isLoading: boolean;
  fields: FilterableField[];
} => {
  const isCurrentUserPartner = useIsCurrentUserPartner();
  const { data: userFields, isLoading } = useTeamMemberFieldsQuery();

  const showableUserFieldsList = useMemo(() => {
    if (isCurrentUserPartner) {
      return [];
    }

    return userFields?.filter(
      (field) => canShowInTeamMemberFieldsSettings(field) && field.isEnabled,
    );
  }, [isCurrentUserPartner, userFields]);
  const showableUserFields = usePopulateUsersMetaInFields(
    showableUserFieldsList ?? [],
  );

  const filterableFields: FilterableField[] = useMemo(
    () =>
      showableUserFields.map((field) => {
        const type = filterableFieldType(field);

        if (type === FieldType.MultiUser || type === FieldType.SingleUser) {
          return {
            ...field,
            type,
            fieldName: camelCase(field.fieldName),
            sourceType: SourceType.User,
          };
        }

        return {
          ...field,
          type,
          fieldName: camelCase(field.fieldName),
          sourceType: SourceType.User,
        };
      }) as FilterableField[],
    [showableUserFields],
  );

  return {
    isLoading,
    fields: filterableFields,
  };
};
